import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// Images
import images from "Assets/images";
// css
import "Components/Common/Modal/style.css";
import "Components/Core/BookingFormModule/new-booking-form.css";

import React from "react";
import { MenuItem } from "@mui/material";
import {
  uploadEquipmentUsageAttachment,
  updateEquipmentUsageAttachment,
} from "Network/Core/EquipmentMaster/equipmentUsage";
import { generateExcelTemplate } from "Library/Utils";
import { useApiCall } from "Hooks/useApiCall";
import { uploadEquipmentRegistry } from "Network/Core/EquipmentMaster/equipmentRegistry";
import toast from "react-hot-toast";
import BulkErrorModal from "../BulkErrorModal";

interface DropdownOption {
  columnName: string;
  options: string[];
}

const ATTACHMENT_ALLOWED_EXTENSIONS = ["xlsx"];

const isAllowedAttachmentFile = (file: File) => {
  const extension = file.name.split(".").pop()?.toLowerCase();
  return !!extension && ATTACHMENT_ALLOWED_EXTENSIONS.includes(extension);
};

interface TemplateConfig {
  headers: string[];
  dropdownColumns: DropdownOption[];
}

export interface MonthlyUsageModelProps {
  equipment?: number;
  project: number | string;
  region: number;
  handleClose?: any;
  type?: string;
  additionalData?: any;
  editingFileId?: number;
}

const FileUploadModal = ({
  equipment,
  project,
  region,
  handleClose,
  type,
  additionalData,
  editingFileId
}: MonthlyUsageModelProps) => {
  const { t } = useTranslation();
  const { call } = useApiCall();
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorData, setErrorData] = useState<any>(null);

  const [sampleCsvData] = useState([
    {
      equipment_ref: "",
      month: "",
      year: "",
      no_of_hours: "",
      litres_or_gallons: "",
    },
  ]);

  const [sampleListOfEquipmentCsvData] = useState([
    {
      scheduled_arrival: "",
      equipment_type: "",
      company: "",
      contact_name: "",
      email_address: "",
      contact_number: "",
      reg_make: "",
      reg_model: "",
      reg_year: "",
      address: "",
      fuel_type: "",
      type_of_usage: "",
      additional_notes: "",
    },
  ]);

  const [downloadLink, setDownloadLink] = useState<string>("");

  useEffect(() => {
    const generateCsvLink = async() => {
      let data: TemplateConfig = {
        headers: Object.keys(type === "listEquipment" ? sampleListOfEquipmentCsvData[0] : sampleCsvData[0]),
        dropdownColumns: [
          {
            columnName: "equipment_type",
            options: additionalData?.equipmentTypeOptions.map((e: any) => e.label)
          },
          {
            columnName: "fuel_type",
            options: additionalData?.fuelTypeOptions.map((e: any) => e.label)
          },
          {
            columnName: "type_of_usage",
            options: additionalData?.typeOfUsageOptions.map((e: any) => e.label)
          }
        ]
      }
      let blob = await generateExcelTemplate(data);
      const url = URL.createObjectURL(blob);
      setDownloadLink(url);
    };

    generateCsvLink();
  }, []);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); // Prevent default behavior to allow drop
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); // Prevent default behavior
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); // Prevent default behavior
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      if (type === "attachment" && !isAllowedAttachmentFile(file)) {
        toast.error(
          t("Translation.file_type_not_supported") ||
            "Unsupported file type. Only .xlsx files are supported"
        );
        return;
      }
      setUploadedFile(file);
    } else {
      console.error("No file was dropped");
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (file) {
      if (type === "attachment" && !isAllowedAttachmentFile(file)) {
        toast.error(
          t("Translation.file_type_not_supported") ||
            "Unsupported file type. Only .xlsx files are supported"
        );
        e.target.value = "";
        return;
      }
      setUploadedFile(file);
    }
  };

  const handleClick = () => {
    const fileInput = document.getElementById(
      "file-upload"
    ) as HTMLInputElement;
    fileInput.click();
  };

  const handleDelete = () => {
    setUploadedFile(null);
  };

  const handleFileUpload = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Implement file upload logic here
    if (uploadedFile) {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      formData.append("file_name", uploadedFile.name);
      formData.append("file_size", uploadedFile.size.toLocaleString());
      if (equipment && type !="listEquipment") {
        formData.append("equipment_usage", equipment.toLocaleString());

        call(
          () =>
            editingFileId
              ? updateEquipmentUsageAttachment(
                  editingFileId,
                  equipment,
                  formData,
                  region,
                  project
                )
              : uploadEquipmentUsageAttachment(
                  equipment,
                  formData,
                  region,
                  project
                ),
          (res: any) => {
            setUploadedFile(null);
            toast.success(t("Translation.file_upload_success") || "File uploaded successfully!");
            handleClose();
          },
          (error: any) => {
            console.error("Equipment usage attachment upload failed:", error);
            const errorResponse = error?.data || error?.response?.data;
            
            if (errorResponse?.errors && errorResponse.errors.length > 1) {
              setErrorData({
                successCount: errorResponse.successCount || 0,
                errorCount: errorResponse.errorCount || errorResponse.errors.length,
                errors: errorResponse.errors
              });
              setShowErrorModal(true);
            } else {
              toast.error(errorResponse?.detail || error?.message || t("Translation.file_upload_failed") || "File upload failed!");
            }
          }
        );
      } else if (type === "listEquipment") {
        formData.append("project", project.toLocaleString());
        call(
          () =>
            uploadEquipmentRegistry(
              formData,
              region,
              project
            ),
          (res: any) => {
            setUploadedFile(null);
            toast.success(t("Translation.file_upload_success") || "File uploaded successfully!");
            handleClose();
          },
          (error: any) => {
            console.error("Equipment registry upload failed:", error);
            const errorResponse = error?.data || error?.response?.data;
            
            if (errorResponse?.errors && errorResponse.errors.length > 1) {
              setErrorData({
                successCount: errorResponse.successCount || 0,
                errorCount: errorResponse.errorCount || errorResponse.errors.length,
                errors: errorResponse.errors
              });
              setShowErrorModal(true);
            } else {
              toast.error(errorResponse?.detail || error?.message || t("Translation.file_upload_failed") || "File upload failed!");
            }
          }
        );
      }
    }
  };
  return (
    <>
      <div
        className="modal fade  show vc-user-invite"
        id="invite_user_Modal"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-scrollable ">
          <div className="modal-content">
            <div className="modal-header">
              <div className="invite-user-header">
                <h2 className="text-left">{editingFileId ? "Replace File" : "File Upload"}</h2>
              </div>
              <div className="invite-user-header vc-invite-from-procore-link">
                <Link onClick={handleClose} to="">
                  {t("Translation.close")}
                  <img src={images.modalCloseIcon} alt="" />
                </Link>
              </div>
            </div>
            <div className="modal-body">
              <div className="file-upload-container">
                <form onSubmit={handleFileUpload}>
                  <div
                    className={`file-input ${
                      dragActive
                        ? "border-primary bg-light"
                        : "border-secondary"
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleClick}
                  >
                    <input
                      id="file-upload"
                      type="file"
                      className="d-none"
                      onChange={handleFileChange}
                      accept={
                        type === "attachment"
                          ? ATTACHMENT_ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",")
                          : ".csv"
                      }
                    />

                    <div className="d-flex flex-column align-items-center text-center w-100">
                      <img
                        src={images.UploadIcon}
                        alt="Upload Icon"
                        className="mb-3"
                        style={{ height: "3rem", width: "3rem" }}
                      />
                      <span className="text-muted">
                        {t("Translation.drag_and_drop")}
                      </span>
                      {uploadedFile && (
                        <div className="mt-3 d-flex align-items-center justify-content-center">
                          <span className="text-primary fw-semibold me-2">
                            Uploaded: {uploadedFile.name}
                          </span>
                          <img
                            src={images.cancel_ic}
                            alt="Cancel Icon"
                            className="cursor-pointer"
                            style={{ height: "1.5rem", width: "1.5rem" }}
                            onClick={handleDelete}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {downloadLink && (
                    <div className="mt-3 d-flex align-items-center justify-content-center">
                      <a
                        href={downloadLink}
                        download={
                          type === "listEquipment"
                            ? "ListOfEquipment_sample.xlsx"
                            : "Equipment_usage_sample.xlsx"
                        }
                        className="block mt-4 text-blue-600 underline"
                      >
                        Download:{" "}
                        {type === "listEquipment"
                          ? "ListOfEquipment_sample.xlsx"
                          : "Equipment_usage_sample.xlsx"}
                      </a>
                    </div>
                  )}

                  <div className="mt-3 d-flex justify-content-end">
                    <button
                      type="submit"
                      className="btn theme-btn"
                      disabled={!uploadedFile}
                    >
                      Upload
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {
        errorData && <BulkErrorModal
          open={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          errorData={errorData}
        />
      }
    </>
  );
};

export default FileUploadModal;
